import { useState, useRef, useEffect } from 'react'
import { Box } from '@mui/material'
import dayjs from 'dayjs'
import {
	CalIc,
	CAL_WORK_START,
	calClosedRanges,
	CAL_STATUS,
	calFmtT,
	calRangeT,
	calStatusOf,
	calInitials,
} from './calShared'
import { fetchMerchantLogo, getMerchantPhotoUrl } from '../../utils/merchantPhotoUtils'

/**
 * Mobile calendar per the Claude Design "Mobile · calendar" (calendar-mobile.jsx):
 * product mobile chrome (hamburger + avatar), a month title pager with a
 * fold-out month grid, a 7-day week strip, groomer filter chips, and three
 * day modes — Schedule (card list), Timeline (tappable lane-per-groomer grid
 * with ghost click-to-book), and Month overview (counts + selected-day peek).
 *
 * Data comes in as visits from calShared buildVisits via `getVisits(day,
 * employeeId|null)`; taps hand off to the shared AppointmentDetailsDialog and
 * AddAppointmentDialog owned by CalendarView.
 */

const TEXT = '#2E2A40'
const MUTED = '#8B85A4'
const BORDER = '#E7E2F3'
const PRIMARY = '#7C6BD9'
const PRIMARY_DEEP = '#5F4EC2'
const PRIMARY_SOFT = '#EDE9FB'
const BG = '#FDFCFF'
const NOW = '#E5484D'
const FONT = "'Outfit', sans-serif"

// px per hour on the mobile timeline (design MC_TL_H)
const MC_TL_H = 66
// fixed row height for the month overview grid — keeps every cell's day number
// and count pill fully visible on short screens (iPhone SE) instead of letting
// flex shrink the rows and clip content; the peek list below absorbs the slack
const MONTH_ROW_H = 52
const TL_GUTTER_W = 40
const TL_GRID_H = 24 * MC_TL_H
const mcalY = (mins) => (mins / 60) * MC_TL_H

const HATCH = 'repeating-linear-gradient(-45deg, rgba(139, 133, 164, 0.09) 0 5px, transparent 5px 11px)'
const PRESS_BG = 'rgba(124, 107, 217, 0.08)'

const btnReset = { border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: FONT, p: 0 }

// design mode-segment glyphs not in CalIc (list / cols / month)
const MC_IC_PATHS = {
	menu: <path d="M2.5 4.5h11M2.5 8h11M2.5 11.5h11" />,
	list: (
		<>
			<path d="M5.6 4.2h8M5.6 8h8M5.6 11.8h8" />
			<circle cx="2.6" cy="4.2" r=".9" fill="currentColor" stroke="none" />
			<circle cx="2.6" cy="8" r=".9" fill="currentColor" stroke="none" />
			<circle cx="2.6" cy="11.8" r=".9" fill="currentColor" stroke="none" />
		</>
	),
	cols: (
		<>
			<rect x="2.5" y="2.5" width="4.6" height="11" rx="1.2" />
			<rect x="8.9" y="2.5" width="4.6" height="11" rx="1.2" />
		</>
	),
	month: (
		<>
			<rect x="2.3" y="2.3" width="11.4" height="11.4" rx="2.4" />
			<path d="M2.3 6.2h11.4M6.1 6.2v7.5M9.9 6.2v7.5" />
		</>
	),
}

function McIc({ name, size = 20, sw = 1.9 }) {
	return (
		<svg
			width={size}
			height={size}
			viewBox="0 0 16 16"
			fill="none"
			stroke="currentColor"
			strokeWidth={sw}
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
			style={{ flex: 'none', display: 'block' }}
		>
			{MC_IC_PATHS[name] || null}
		</svg>
	)
}

const employeeName = (emp) => `${emp?.first_name || ''} ${emp?.last_name || ''}`.trim()

const dayLabel = (d) => d.format('ddd, MMM D')

// month grid cells padded to full weeks (same math as CalendarToolbar's date jump)
function monthCells(anchor) {
	const first = anchor.startOf('month')
	const gridStart = first.subtract(first.day(), 'day')
	const weeks = Math.ceil((first.day() + anchor.daysInMonth()) / 7)
	return Array.from({ length: weeks * 7 }, (_, i) => {
		const d = gridStart.add(i, 'day')
		return { date: d, inMonth: d.isSame(first, 'month') }
	})
}

// greedy lane assignment for overlapping timeline visits (design calLanes):
// visits split lanes only within their own overlapping cluster, so a lone
// morning visit stays full-width even when the afternoon has overlaps
function laneVisits(visits) {
	const out = []
	let cluster = null
	let clusterEnd = -1
	visits.forEach(v => {
		if (v.sm >= clusterEnd) {
			cluster = []
			out.push(cluster)
			clusterEnd = v.sm + v.mins
		} else {
			clusterEnd = Math.max(clusterEnd, v.sm + v.mins)
		}
		cluster.push(v)
	})
	return out.flatMap(cl => {
		const laneEnds = []
		const placed = cl.map(v => {
			let lane = laneEnds.findIndex(e => e <= v.sm)
			if (lane === -1) {
				lane = laneEnds.length
				laneEnds.push(v.sm + v.mins)
			} else {
				laneEnds[lane] = v.sm + v.mins
			}
			return { v, lane }
		})
		return placed.map(p => ({ ...p, lanes: laneEnds.length }))
	})
}

const DEFAULT_LOGO = '/petStoreDefault.svg'

// ── header: hamburger / avatar chrome + month title pager ───────────
function McHeader({ merchant, title, sub, monthOpen, monthMode, onToggleMonth, onMove, onToday, todayDisabled, onMenu }) {
	const [logoUrl, setLogoUrl] = useState(DEFAULT_LOGO)

	useEffect(() => {
		let alive = true
		async function loadLogo() {
			if (!merchant?.id) return
			const logoPhoto = await fetchMerchantLogo(merchant.id)
			if (!alive) return
			setLogoUrl((logoPhoto && getMerchantPhotoUrl(logoPhoto)) || DEFAULT_LOGO)
		}
		loadLogo()
		return () => { alive = false }
	}, [merchant?.id])

	const navBtnSx = {
		...btnReset, width: 40, height: 40, flex: 'none', borderRadius: '11px', color: MUTED,
		display: 'flex', alignItems: 'center', justifyContent: 'center',
		'&:active': { bgcolor: PRESS_BG, color: PRIMARY_DEEP },
	}

	return (
		<Box sx={{ flex: 'none', p: '10px 16px 4px', bgcolor: BG }}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
				<Box component="button" type="button" aria-label="Open menu" onClick={onMenu}
					sx={{ ...btnReset, width: 40, height: 40, flex: 'none', ml: '-8px', color: TEXT, borderRadius: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center', '&:active': { bgcolor: '#F5F3FB' } }}>
					<McIc name="menu" size={21} />
				</Box>
				<Box sx={{ ml: 'auto', width: 38, height: 38, flex: 'none', borderRadius: '50%', overflow: 'hidden', bgcolor: PRIMARY_SOFT }}>
					<img
						src={logoUrl}
						alt="Store logo"
						style={{ width: '100%', height: '100%', objectFit: 'cover' }}
						onError={(e) => { if (e.target.src !== DEFAULT_LOGO) e.target.src = DEFAULT_LOGO }}
					/>
				</Box>
			</Box>
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', mt: '2px' }}>
				<Box sx={{ display: 'flex', alignItems: 'center', ml: '-10px', minWidth: 0 }}>
					<Box component="button" type="button" aria-label={(monthOpen || monthMode) ? 'Previous month' : 'Previous week'} onClick={() => onMove(-1)} sx={navBtnSx}>
						<CalIc name="chevL" size={17} sw={1.9} />
					</Box>
					<Box component="button" type="button" aria-expanded={monthOpen} onClick={monthMode ? undefined : onToggleMonth}
						sx={{
							...btnReset, display: 'flex', alignItems: 'baseline', gap: '6px', minWidth: 0,
							whiteSpace: 'nowrap', p: '7px 8px', borderRadius: '11px',
							...(monthMode ? { pointerEvents: 'none' } : {}),
							...(monthOpen ? { bgcolor: PRIMARY_SOFT } : {}),
							'&:active': { bgcolor: PRESS_BG },
							'& svg': { alignSelf: 'center', color: monthOpen ? PRIMARY_DEEP : MUTED, transition: 'transform 0.15s ease', transform: monthOpen ? 'rotate(180deg)' : 'none' },
						}}>
						<Box component="strong" sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', color: TEXT }}>{title}</Box>
						<Box component="span" sx={{ fontSize: '13.5px', fontWeight: 650, color: MUTED }}>{sub}</Box>
						{monthMode ? null : <CalIc name="chevD" size={13} sw={2.1} />}
					</Box>
					<Box component="button" type="button" aria-label={(monthOpen || monthMode) ? 'Next month' : 'Next week'} onClick={() => onMove(1)} sx={navBtnSx}>
						<CalIc name="chevR" size={17} sw={1.9} />
					</Box>
				</Box>
				<Box component="button" type="button" disabled={todayDisabled} onClick={onToday}
					sx={{
						...btnReset, height: 36, px: '14px', flex: 'none', border: `1.5px solid ${BORDER}`, borderRadius: '11px',
						bgcolor: '#FFFFFF', fontSize: '13px', fontWeight: 650, color: TEXT,
						'&:active': { borderColor: PRIMARY, color: PRIMARY_DEEP },
						'&:disabled': { opacity: 0.4, pointerEvents: 'none' },
					}}>
					Today
				</Box>
			</Box>
		</Box>
	)
}

// day-number circle shared by the strips and grids
const dayNumSx = (isSel, isToday, size = 32) => ({
	width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center',
	borderRadius: '50%', fontSize: size >= 32 ? '14.5px' : '13px', fontWeight: 750, color: TEXT,
	...(isToday ? { color: PRIMARY_DEEP, boxShadow: `inset 0 0 0 1.5px ${PRIMARY}` } : {}),
	...(isSel ? {
		bgcolor: PRIMARY, color: '#FFFFFF', fontWeight: 800,
		boxShadow: '0 2px 6px rgba(124, 107, 217, 0.4)',
	} : {}),
})

const slideAnim = (dir) => ({
	'@keyframes mcalInR': { from: { opacity: 0.3, transform: 'translateX(14px)' }, to: { opacity: 1, transform: 'none' } },
	'@keyframes mcalInL': { from: { opacity: 0.3, transform: 'translateX(-14px)' }, to: { opacity: 1, transform: 'none' } },
	animation: `${dir < 0 ? 'mcalInL' : 'mcalInR'} 0.2s ease-out`,
	'@media (prefers-reduced-motion: reduce)': { animation: 'none' },
})

// ── 7-day week strip ────────────────────────────────────────────────
function McWeekStrip({ weekStart, sel, now, dir, onPick }) {
	const days = Array.from({ length: 7 }, (_, n) => weekStart.add(n, 'day'))
	return (
		<Box key={weekStart.format('YYYY-MM-DD')} role="group" aria-label="Pick a day"
			sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', p: '6px 10px 2px', flex: 'none', ...slideAnim(dir) }}>
			{days.map(d => {
				const isSel = d.isSame(sel, 'day')
				const isToday = d.isSame(now, 'day')
				return (
					<Box component="button" type="button" key={d.format('YYYY-MM-DD')} onClick={() => onPick(d)}
						sx={{
							...btnReset, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px',
							p: '6px 0 5px', borderRadius: '13px', minHeight: 60, justifyContent: 'center',
							'&:active': { bgcolor: PRESS_BG },
						}}>
						<Box component="span" sx={{ fontSize: '10.5px', fontWeight: 750, letterSpacing: '0.06em', color: isToday ? PRIMARY_DEEP : MUTED }}>
							{d.format('dd').slice(0, 1)}
						</Box>
						<Box component="span" sx={dayNumSx(isSel, isToday)}>{d.date()}</Box>
					</Box>
				)
			})}
		</Box>
	)
}

const weekdayHeader = (
	<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', pb: '2px' }}>
		{['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((w, i) => (
			<Box component="span" key={i} sx={{ textAlign: 'center', fontSize: '10px', fontWeight: 750, letterSpacing: '0.06em', color: '#A49DBD' }}>{w}</Box>
		))}
	</Box>
)

// ── fold-out month grid (title tap) ─────────────────────────────────
function McMonthGrid({ monthAnchor, sel, now, onPick }) {
	const cells = monthCells(monthAnchor)
	return (
		<Box sx={{
			p: '6px 10px 4px', flex: 'none',
			'@keyframes mcalUnfold': { from: { opacity: 0, transform: 'translateY(-6px)' }, to: { opacity: 1, transform: 'none' } },
			animation: 'mcalUnfold 0.18s ease-out',
			'@media (prefers-reduced-motion: reduce)': { animation: 'none' },
		}}>
			{weekdayHeader}
			<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
				{cells.map(c => {
					const isSel = c.date.isSame(sel, 'day')
					const isToday = c.date.isSame(now, 'day')
					return (
						<Box component="button" type="button" key={c.date.format('YYYY-MM-DD')} onClick={() => onPick(c.date)}
							sx={{
								...btnReset, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px',
								p: '3px 0 2px', borderRadius: '11px', minHeight: 40, justifyContent: 'center',
								'&:active': { bgcolor: PRESS_BG },
							}}>
							<Box component="span" sx={{ ...dayNumSx(isSel, isToday, 28), ...(c.inMonth || isSel ? {} : { color: '#C7C0DA' }) }}>
								{c.date.date()}
							</Box>
						</Box>
					)
				})}
			</Box>
		</Box>
	)
}

// ── employee filter chips ───────────────────────────────────────────
function McGChips({ employees, selectedEmployees, onToggleEmployee, onClearEmployeeFilter }) {
	const allOn = selectedEmployees.length === 0
	return (
		<Box role="group" aria-label="Filter by groomer" sx={{
			display: 'flex', alignItems: 'center', gap: '6px', p: '7px 16px 9px', overflowX: 'auto',
			flex: 'none', scrollbarWidth: 'none', borderBottom: '1px solid #F1EFF8',
			'&::-webkit-scrollbar': { display: 'none' },
		}}>
			<Box component="button" type="button" aria-pressed={allOn} onClick={onClearEmployeeFilter}
				sx={{
					...btnReset, height: 36, px: '15px', flex: 'none', border: `1.5px solid ${allOn ? 'transparent' : BORDER}`,
					borderRadius: '99px', bgcolor: allOn ? PRIMARY_SOFT : '#FFFFFF',
					fontSize: '12.5px', fontWeight: 700, color: allOn ? PRIMARY_DEEP : MUTED,
				}}>
				All
			</Box>
			{employees.map(emp => {
				const on = selectedEmployees.some(e => e.id === emp.id)
				const name = employeeName(emp)
				return (
					<Box component="button" type="button" key={emp.id} aria-pressed={on} onClick={() => onToggleEmployee(emp)}
						sx={{
							...btnReset, display: 'inline-flex', alignItems: 'center', gap: '6px', height: 36,
							p: '0 12px 0 4px', flex: 'none', border: `1.5px solid ${on ? PRIMARY : BORDER}`,
							borderRadius: '99px', bgcolor: on ? PRIMARY_SOFT : '#FFFFFF',
							fontSize: '12.5px', fontWeight: 700, color: on ? PRIMARY_DEEP : MUTED,
						}}>
						<Box component="span" sx={{
							width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center',
							justifyContent: 'center', fontSize: '10px', fontWeight: 800, letterSpacing: '0.02em',
							bgcolor: '#F0EEF6', color: '#5C5578',
						}}>
							{calInitials(name)}
						</Box>
						<span>{emp.first_name || name}</span>
					</Box>
				)
			})}
		</Box>
	)
}

// ── status chip on cards ────────────────────────────────────────────
function McStatusChip({ st }) {
	if (st !== 'in' && st !== 'noshow' && st !== 'pending') return null
	const s = CAL_STATUS[st]
	return (
		<>
			<Box component="em" sx={{
				flex: 'none', fontStyle: 'normal', height: 21, px: '8px', borderRadius: '99px',
				fontSize: '10.5px', fontWeight: 750, display: 'inline-flex', alignItems: 'center',
				bgcolor: s.bg, color: s.fg,
			}}>
				{s.label}
			</Box>
			{st === 'in' && (
				<Box component="i" sx={{
					width: 7, height: 7, borderRadius: '50%', bgcolor: '#2F8A5B', flex: 'none',
					'@keyframes mcalPulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.35 } },
					animation: 'mcalPulse 2s infinite',
					'@media (prefers-reduced-motion: reduce)': { animation: 'none' },
				}} />
			)}
		</>
	)
}

// ── schedule card (list mode) ───────────────────────────────────────
function McCard({ visit, now, onOpen }) {
	const st = calStatusOf(visit, now)
	const cs = visit.colorSet
	// no-show (design .mcal-card.noshow): hollow white card, soft red outline,
	// struck pet name, muted service/price
	const noshow = st === 'noshow'
	const body = noshow ? '#A39CB8' : '#5C5578'
	return (
		<Box component="button" type="button" onClick={() => onOpen(visit)}
			sx={{
				...btnReset, position: 'relative', display: 'flex', width: '100%', textAlign: 'left',
				borderRadius: '13px', p: '10px 13px 9px',
				boxShadow: '0 1px 2px rgba(46, 42, 64, 0.05)',
				...(visit.multi
					? { bgcolor: '#FFFFFF', border: '1px solid rgba(124, 107, 217, 0.16)', pl: '16px' }
					: noshow
						? { bgcolor: '#FFFFFF', borderLeft: '3.5px solid #CF6F69' }
						: { bgcolor: cs.soft, borderLeft: `3.5px solid ${cs.accent}` }),
				...(st === 'done' ? { opacity: 0.58 } : {}),
				...(noshow ? { boxShadow: 'inset 0 0 0 1.5px #EDCCC9, 0 1px 2px rgba(46, 42, 64, 0.03)' } : {}),
				'&:active': { filter: 'brightness(0.97)' },
			}}>
			{visit.multi && (
				<Box sx={{
					position: 'absolute', left: 0, top: 0, bottom: 0, width: '4.5px', display: 'flex',
					flexDirection: 'column', borderRadius: '12px 0 0 12px', overflow: 'hidden',
				}}>
					{visit.pets.map((p, i) => <Box key={i} sx={{ flex: 1, bgcolor: p.colorSet.accent }} />)}
				</Box>
			)}
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0, width: '100%' }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 750, letterSpacing: '0.01em', color: visit.multi ? '#5C5578' : noshow ? '#8B85A4' : cs.deep }}>
					<Box component="span" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
						{calRangeT(visit.start, visit.end)}
					</Box>
					{st === 'done' && (
						<Box component="i" sx={{
							width: 15, height: 15, borderRadius: '50%', bgcolor: 'rgba(46, 42, 64, 0.12)', color: '#5C5578',
							display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none',
						}}>
							<CalIc name="check" size={10} sw={2.4} />
						</Box>
					)}
					<McStatusChip st={st} />
				</Box>
				{visit.multi ? (
					visit.pets.map((p, i) => (
						<Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '7px', minWidth: 0 }}>
							<Box component="i" sx={{ width: 7, height: 7, borderRadius: '50%', flex: 'none', bgcolor: p.colorSet.accent }} />
							<Box component="strong" sx={{ fontSize: '13.5px', fontWeight: 750, color: TEXT, whiteSpace: 'nowrap' }}>{p.pet}</Box>
							<Box component="span" sx={{ fontSize: '12px', fontWeight: 650, color: body, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.svc}</Box>
							<Box component="em" sx={{ ml: 'auto', fontStyle: 'normal', fontSize: '12px', fontWeight: 750, color: body, flex: 'none' }}>{p.priceLabel}</Box>
						</Box>
					))
				) : (
					<>
						<Box component="span" sx={{ fontSize: '15px', fontWeight: 750, color: TEXT, letterSpacing: '-0.01em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
							<Box component="strong" sx={{
								fontWeight: 'inherit',
								...(noshow ? { color: '#6E6886', textDecoration: 'line-through', textDecorationColor: '#D08F8A', textDecorationThickness: '1.5px' } : {}),
							}}>
								{visit.pet}
							</Box>{' '}
							<Box component="span" sx={{ fontSize: '12px', fontWeight: 600, color: '#7A7492' }}>{visit.client}</Box>
						</Box>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', minWidth: 0 }}>
							<Box component="span" sx={{ fontSize: '12px', fontWeight: 650, color: body, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{visit.svcName}</Box>
							<Box component="em" sx={{ ml: 'auto', fontStyle: 'normal', fontSize: '12px', fontWeight: 750, color: body, flex: 'none' }}>{visit.priceLabel}</Box>
						</Box>
					</>
				)}
				{visit.employee && (
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mt: '3px', fontSize: '11px', fontWeight: 700, color: '#6E6688' }}>
						<Box component="i" sx={{ width: 7, height: 7, borderRadius: '50%', flex: 'none', bgcolor: '#C7C0DA' }} />
						{visit.employee}
					</Box>
				)}
			</Box>
		</Box>
	)
}

// ── now separator (list mode) ───────────────────────────────────────
function McNowLine({ now }) {
	return (
		<Box aria-label="Current time" sx={{ display: 'flex', alignItems: 'center', gap: '7px', my: '2px' }}>
			<Box component="em" sx={{
				fontStyle: 'normal', bgcolor: NOW, color: '#FFFFFF', fontSize: '10px', fontWeight: 800,
				p: '2.5px 7px', borderRadius: '6px', letterSpacing: '0.02em', flex: 'none',
			}}>
				{calFmtT(now, true)}
			</Box>
			<Box component="i" sx={{ flex: 1, borderTop: `2px solid ${NOW}`, borderRadius: '2px' }} />
		</Box>
	)
}

// ── day schedule list ───────────────────────────────────────────────
function McDayList({ visits, isToday, now, onOpen }) {
	const nowMins = now.hour() * 60 + now.minute()
	let nowShown = false
	return (
		<Box sx={{ p: '2px 16px 96px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
			{visits.length === 0 ? (
				<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', p: '46px 0', color: '#A49DBD' }}>
					<CalIc name="paw" size={22} sw={1.3} />
					<Box component="strong" sx={{ fontSize: '14.5px', fontWeight: 750, color: MUTED, mt: '6px' }}>Nothing booked</Box>
					<Box component="span" sx={{ fontSize: '12.5px', fontWeight: 600 }}>This day is wide open.</Box>
				</Box>
			) : visits.map(visit => {
				const nowHere = isToday && !nowShown && visit.sm >= nowMins
				if (nowHere) nowShown = true
				return (
					<Box key={visit.key} sx={{ display: 'contents' }}>
						{nowHere && <McNowLine now={now} />}
						<McCard visit={visit} now={now} onOpen={onOpen} />
					</Box>
				)
			})}
			{isToday && !nowShown && visits.length > 0 && <McNowLine now={now} />}
		</Box>
	)
}

// ── timeline mode: tappable grid, lane per groomer ──────────────────
function McTlCard({ visit, lane, lanes, now, onOpen }) {
	const st = calStatusOf(visit, now)
	const cs = visit.colorSet
	const h = Math.max((visit.mins / 60) * MC_TL_H - 2, 16)
	const tiny = h < 34
	const short = h < 56
	return (
		<Box component="button" type="button"
			onClick={(e) => { e.stopPropagation(); onOpen(visit) }}
			sx={{
				...btnReset, position: 'absolute', zIndex: 4, display: 'flex', flexDirection: 'column',
				textAlign: 'left', borderRadius: '8px', p: '3px 6px 2px 7px', overflow: 'hidden',
				boxShadow: '0 1px 2px rgba(46, 42, 64, 0.05)',
				top: mcalY(visit.sm), height: h,
				left: `calc(${(lane / lanes) * 100}% + 2px)`,
				width: `calc(${(1 / lanes) * 100}% - 4px)`,
				bgcolor: visit.multi ? '#FFFFFF' : cs.soft,
				borderLeft: `3px solid ${visit.multi ? 'rgba(124, 107, 217, 0.4)' : cs.accent}`,
				...(st === 'done' ? { opacity: 0.55 } : {}),
				...(tiny ? { flexDirection: 'row', alignItems: 'center', gap: '5px', pt: 0, pb: 0 } : {}),
				'&:active': { filter: 'brightness(0.96)' },
			}}>
			{visit.multi && (
				<Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '3px', display: 'flex', flexDirection: 'column' }}>
					{visit.pets.map((p, i) => <Box key={i} sx={{ flex: 1, bgcolor: p.colorSet.accent }} />)}
				</Box>
			)}
			<Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '9.5px', fontWeight: 750, letterSpacing: '0.01em', whiteSpace: 'nowrap', flex: 'none', color: visit.multi ? '#5C5578' : cs.deep }}>
				{calFmtT(visit.start, false)}
				{st === 'in' && (
					<Box component="i" sx={{
						width: 6, height: 6, borderRadius: '50%', bgcolor: '#2F8A5B', flex: 'none',
						'@keyframes mcalPulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.35 } },
						animation: 'mcalPulse 2s infinite',
						'@media (prefers-reduced-motion: reduce)': { animation: 'none' },
					}} />
				)}
			</Box>
			<Box component="span" sx={{ fontSize: '11px', fontWeight: 750, color: TEXT, letterSpacing: '-0.01em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
				{visit.pet}
			</Box>
			{!short && (
				<Box component="span" sx={{ fontSize: '9.5px', fontWeight: 650, color: '#5C5578', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
					{visit.svcName}
				</Box>
			)}
		</Box>
	)
}

function McTimeline({ sel, isToday, now, lanes, getVisits, ghost, onTap, onGhost, onOpen, merchant }) {
	// Closed time for the selected day (weekly hours + recurring closed dates).
	const dayClosed = calClosedRanges(sel, merchant)
	const nowMins = now.hour() * 60 + now.minute()
	const hours = Array.from({ length: 24 }, (_, h) => h)
	const gridCols = `${TL_GUTTER_W}px repeat(${lanes.length}, 1fr)`

	const laneTap = (emp) => (e) => {
		if (e.target.closest && e.target.closest('button')) return
		const rect = e.currentTarget.getBoundingClientRect()
		const y = (e.clientY - rect.top) * (TL_GRID_H / rect.height)
		let sm = Math.floor(y / (MC_TL_H / 2)) * 30 // snap to 30 min
		sm = Math.max(0, Math.min(sm, 24 * 60 - 30))
		onTap({ employeeId: emp?.id || null, sm })
	}

	return (
		<Box sx={{ p: '0 12px 96px 8px', fontFamily: FONT }}>
			{/* lane heads */}
			<Box sx={{
				display: 'grid', gridTemplateColumns: gridCols, position: 'sticky', top: 0, zIndex: 20,
				bgcolor: BG, p: '4px 0 6px', borderBottom: `1.5px solid ${BORDER}`,
			}}>
				<span />
				{lanes.map(emp => {
					const name = emp ? employeeName(emp) : 'All'
					return (
						<Box key={emp?.id || 'all'} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', minWidth: 0, px: '2px' }}>
							<Box component="i" sx={{
								width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center',
								justifyContent: 'center', fontSize: '9.5px', fontWeight: 800, fontStyle: 'normal',
								bgcolor: '#F0EEF6', color: '#5C5578',
							}}>
								{calInitials(name)}
							</Box>
							<Box component="em" sx={{ fontStyle: 'normal', fontSize: '10.5px', fontWeight: 750, color: TEXT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
								{emp ? (emp.first_name || name) : 'All'}
							</Box>
						</Box>
					)
				})}
			</Box>
			{/* body */}
			<Box sx={{ display: 'grid', gridTemplateColumns: gridCols, position: 'relative', height: TL_GRID_H }}>
				<Box sx={{ position: 'relative' }}>
					{hours.map(h => h === 0 ? null : (
						<Box key={h} component="span" sx={{
							position: 'absolute', right: 6, top: mcalY(h * 60), transform: 'translateY(-50%)',
							fontSize: '9.5px', fontWeight: 650, color: '#A49DBD', whiteSpace: 'nowrap',
						}}>
							{calFmtT(dayjs().startOf('day').hour(h), true)}
						</Box>
					))}
					{isToday && (
						<Box component="span" sx={{
							position: 'absolute', right: 4, top: mcalY(nowMins), transform: 'translateY(-50%)',
							bgcolor: NOW, color: '#FFFFFF', fontSize: '9px', fontWeight: 800, p: '2px 5px',
							borderRadius: '5px', zIndex: 31, letterSpacing: '0.02em', whiteSpace: 'nowrap',
						}}>
							{calFmtT(now, false)}
						</Box>
					)}
				</Box>
				{lanes.map(emp => {
					const visits = getVisits(sel, emp?.id || null)
					const placed = laneVisits(visits)
					return (
						<Box key={emp?.id || 'all'} onClick={laneTap(emp)} sx={{
							position: 'relative', borderLeft: '1px solid #F1EFF8', minWidth: 0, cursor: 'pointer',
							backgroundImage: `
								linear-gradient(to bottom, #EFECF6 1px, transparent 1px),
								linear-gradient(to bottom, transparent 33px, #F6F4FA 33px, #F6F4FA 34px, transparent 34px)
							`,
							backgroundSize: `100% ${MC_TL_H}px, 100% ${MC_TL_H}px`,
						}}>
							{dayClosed.ranges.map((r, ri) => (
								<Box key={ri} sx={{
									position: 'absolute', left: 0, right: 0, zIndex: 1, pointerEvents: 'none',
									top: mcalY(r.from), height: mcalY(r.to) - mcalY(r.from), background: HATCH,
								}} />
							))}
							{placed.map(({ v, lane, lanes: n }) => (
								<McTlCard key={v.key} visit={v} lane={lane} lanes={n} now={now} onOpen={onOpen} />
							))}
							{ghost && ghost.employeeId === (emp?.id || null) && (
								<Box component="button" type="button"
									onClick={(e) => { e.stopPropagation(); onGhost() }}
									sx={{
										...btnReset, position: 'absolute', left: 2, right: 2, zIndex: 6,
										top: mcalY(ghost.sm), height: MC_TL_H / 2 - 2,
										border: '1.5px dashed #A99BE8', bgcolor: 'rgba(124, 107, 217, 0.08)',
										borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '4px', px: '6px',
										color: PRIMARY_DEEP, fontSize: '10px', fontWeight: 750, whiteSpace: 'nowrap', overflow: 'hidden',
										'@keyframes mcalGhostIn': { from: { opacity: 0, transform: 'scale(0.96)' }, to: { opacity: 1, transform: 'none' } },
										animation: 'mcalGhostIn 0.15s ease-out',
										'@media (prefers-reduced-motion: reduce)': { animation: 'none' },
									}}>
									<CalIc name="plus" size={12} sw={2.2} />
									<Box component="span" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'left' }}>
										{calFmtT(dayjs().startOf('day').add(ghost.sm, 'minute'), true)}
									</Box>
									<Box component="em" sx={{ fontStyle: 'normal', flex: 'none', bgcolor: PRIMARY, color: '#FFFFFF', p: '3px 8px', borderRadius: '99px', fontSize: '9.5px', fontWeight: 800 }}>
										Book
									</Box>
								</Box>
							)}
						</Box>
					)
				})}
				{isToday && (
					<Box sx={{
						position: 'absolute', left: TL_GUTTER_W, right: 0, top: mcalY(nowMins),
						borderTop: `2px solid ${NOW}`, zIndex: 30, pointerEvents: 'none',
						'&::before': {
							content: '""', position: 'absolute', left: '-3px', top: '-5px', width: 8, height: 8,
							borderRadius: '50%', bgcolor: NOW, boxShadow: '0 0 0 2px #FFFFFF',
						},
					}} />
				)}
			</Box>
		</Box>
	)
}

// ── month overview mode: full month + selected-day peek ─────────────
function McMonthView({ sel, now, dir, getVisits, onPick, onOpenDay, onOpenAppt, onBook }) {
	const cells = monthCells(sel.startOf('month'))
	const visits = getVisits(sel, null)
	const isToday = sel.isSame(now, 'day')
	return (
		<Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', p: '0 10px' }}>
			<Box sx={{ p: '5px 0 3px', flex: 'none' }}>{weekdayHeader}</Box>
			<Box key={sel.format('YYYY-MM')} role="group" aria-label="Month overview" sx={{
				flex: 'none', display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
				gridTemplateRows: `repeat(${cells.length / 7}, ${MONTH_ROW_H}px)`, borderTop: '1px solid #EFECF6',
				...slideAnim(dir),
			}}>
				{cells.map(c => {
					const count = getVisits(c.date, null).length
					const isSel = c.date.isSame(sel, 'day')
					const isCellToday = c.date.isSame(now, 'day')
					return (
						<Box component="button" type="button" key={c.date.format('YYYY-MM-DD')} onClick={() => onPick(c.date)}
							aria-label={`${dayLabel(c.date)} · ${count} ${count === 1 ? 'appointment' : 'appointments'}`}
							sx={{
								...btnReset, borderBottom: '1px solid #F1EFF8', borderRadius: 0, p: '5px 2px 3px',
								display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center',
								justifyContent: 'flex-start', minWidth: 0, overflow: 'hidden',
								'&:active': { bgcolor: PRESS_BG },
							}}>
							<Box component="span" sx={{ ...dayNumSx(isSel, isCellToday, 22), fontSize: '12px', ...(c.inMonth || isSel ? {} : { color: '#C7C0DA' }) }}>
								{c.date.date()}
							</Box>
							{count > 0 && (
								<Box component="span" aria-hidden="true" sx={{
									display: 'inline-flex', alignItems: 'center', gap: '3.5px', flex: 'none',
									maxWidth: 'calc(100% - 4px)', p: '3px 8px', borderRadius: '999px',
									bgcolor: '#EFECFB', color: PRIMARY_DEEP, ...(c.inMonth ? {} : { opacity: 0.5 }),
								}}>
									<CalIc name="paw" size={9.5} sw={1.7} />
									<Box component="em" sx={{ fontStyle: 'normal', fontSize: '10.5px', fontWeight: 750, lineHeight: 1 }}>{count}</Box>
								</Box>
							)}
						</Box>
					)
				})}
			</Box>
			{/* selected-day peek — hugs its content, growing up to the height left
			    under the month grid; past that the list scrolls */}
			<Box sx={{
				flex: '0 1 auto', minHeight: 0, m: '7px 2px 12px', bgcolor: '#FFFFFF', border: '1px solid rgba(124, 107, 217, 0.12)',
				borderRadius: '16px', boxShadow: '0 10px 26px rgba(46, 42, 64, 0.09)', display: 'flex',
				flexDirection: 'column', overflow: 'hidden',
			}}>
				<Box sx={{ display: 'flex', alignItems: 'baseline', gap: '7px', p: '12px 14px 8px', flex: 'none' }}>
					<Box component="strong" sx={{ fontSize: '13.5px', fontWeight: 750, color: TEXT, whiteSpace: 'nowrap' }}>
						{dayLabel(sel)}{isToday ? ' · Today' : ''}
					</Box>
					<Box component="span" sx={{ fontSize: '11.5px', fontWeight: 650, color: MUTED, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
						{visits.length === 0 ? 'No appointments' : `${visits.length} ${visits.length === 1 ? 'appointment' : 'appointments'}`}
					</Box>
					<Box component="button" type="button" onClick={onOpenDay}
						sx={{
							...btnReset, ml: 'auto', flex: 'none', display: 'inline-flex', alignItems: 'center', gap: '3px',
							color: PRIMARY_DEEP, fontSize: '12px', fontWeight: 750, p: '4px 2px 4px 8px', borderRadius: '8px',
							'&:active': { bgcolor: PRIMARY_SOFT },
						}}>
						Open day<CalIc name="chevR" size={12} sw={2.2} />
					</Box>
				</Box>
				<Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: '0 8px 10px', display: 'flex', flexDirection: 'column', gap: '1px' }}>
					{visits.length === 0 ? (
						<Box sx={{ fontSize: '12px', fontWeight: 650, color: MUTED, p: '2px 8px 8px' }}>
							Nothing booked — this day is wide open.
						</Box>
					) : visits.map(visit => (
						<Box component="button" type="button" key={visit.key} onClick={() => onOpenAppt(visit)}
							sx={{
								...btnReset, display: 'flex', alignItems: 'center', gap: '9px', p: '6px', borderRadius: '10px',
								textAlign: 'left', minWidth: 0,
								...(calStatusOf(visit, now) === 'done' ? { opacity: 0.55 } : {}),
								'&:active': { bgcolor: '#F5F3FB' },
							}}>
							<Box component="em" sx={{ fontStyle: 'normal', width: 56, flex: 'none', fontSize: '11px', fontWeight: 750, letterSpacing: '0.01em', color: visit.multi ? PRIMARY_DEEP : visit.colorSet.deep }}>
								{calFmtT(visit.start)}
							</Box>
							<Box component="i" sx={{ width: 7, height: 7, borderRadius: '50%', flex: 'none', bgcolor: visit.multi ? PRIMARY : visit.colorSet.accent }} />
							<Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0, flex: 1 }}>
								{visit.multi ? visit.pets.map((p, i) => (
									<Box key={i} sx={{ display: 'flex', alignItems: 'baseline', gap: '6px', minWidth: 0 }}>
										<Box component="strong" sx={{ flex: 'none', fontSize: '13px', fontWeight: 750, color: TEXT }}>{p.pet}</Box>
										<Box component="span" sx={{ flex: 1, fontSize: '11px', fontWeight: 650, color: '#5C5578', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.svc}</Box>
									</Box>
								)) : (
									<>
										<Box component="strong" sx={{ fontSize: '13px', fontWeight: 750, color: TEXT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{visit.pet}</Box>
										<Box component="span" sx={{ fontSize: '11px', fontWeight: 650, color: '#5C5578', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{visit.svcName}</Box>
									</>
								)}
							</Box>
							<Box component="u" sx={{ textDecoration: 'none', fontSize: '12px', fontWeight: 750, color: '#5C5578', flex: 'none' }}>{visit.priceLabel}</Box>
						</Box>
					))}
					<Box component="button" type="button" onClick={onBook}
						sx={{
							...btnReset, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
							height: 38, flex: 'none', border: '1.5px dashed #D9D2EA', borderRadius: '10px',
							color: MUTED, fontSize: '12px', fontWeight: 750, mt: '3px',
							'&:active': { borderColor: PRIMARY, color: PRIMARY_DEEP },
						}}>
						<CalIc name="plus" size={13} sw={2.2} />Add appointment
					</Box>
				</Box>
			</Box>
		</Box>
	)
}

// ── main mobile calendar ────────────────────────────────────────────
export default function CalendarMobile({
	merchant,
	now,                 // dayjs in merchant tz
	calendarDate,        // dayjs — selected day
	onDateChange,
	mode,                // 'list' | 'grid' | 'month'
	onModeChange,
	employees,
	selectedEmployees,
	onToggleEmployee,
	onClearEmployeeFilter,
	getVisits,           // (day, employeeId|null) => visits (calShared buildVisits)
	loading,
	onVisitClick,
	onAddAppointment,    // ({ date, time?, employee? })
	onOpenSidebar,
}) {
	const sel = calendarDate
	const [monthOpen, setMonthOpen] = useState(false)
	const [monthAnchor, setMonthAnchor] = useState(() => calendarDate.startOf('month'))
	const [dir, setDir] = useState(1)
	const [ghost, setGhost] = useState(null) // { employeeId, sm }
	const scrollRef = useRef(null)

	const isToday = sel.isSame(now, 'day')
	const weekStart = sel.startOf('week')
	const titleAnchor = monthOpen ? monthAnchor : sel
	const displayEmployees = selectedEmployees.length > 0 ? selectedEmployees : employees
	const lanes = displayEmployees.length > 0 ? displayEmployees : [null]
	const dayVisits = getVisits(sel, null)

	// timeline starts scrolled near now (today) or opening time; list starts at top
	useEffect(() => {
		const el = scrollRef.current
		if (!el) return
		if (mode === 'grid') {
			const target = ghost ? mcalY(ghost.sm) : isToday ? mcalY(now.hour() * 60 + now.minute()) : mcalY(CAL_WORK_START * 60)
			el.scrollTop = Math.max(0, target - 120)
		}
		if (mode === 'list') el.scrollTop = 0
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mode, sel.format('YYYY-MM-DD')])

	const move = (n) => {
		setDir(n)
		setGhost(null)
		if (mode === 'month') {
			const next = sel.startOf('month').add(n, 'month')
			onDateChange(next.isSame(now, 'month') ? now.startOf('day') : next)
		} else if (monthOpen) {
			setMonthAnchor(monthAnchor.add(n, 'month'))
		} else {
			onDateChange(sel.add(n * 7, 'day'))
		}
	}
	const goToday = () => {
		onDateChange(now.startOf('day'))
		setMonthAnchor(now.startOf('month'))
		setMonthOpen(false)
		setGhost(null)
	}
	const pickDay = (d) => {
		onDateChange(d)
		setGhost(null)
		if (monthOpen) setMonthOpen(false)
	}
	const bookOn = () => {
		setGhost(null)
		onAddAppointment({ date: sel.format('YYYY-MM-DD') })
	}
	const bookSlot = () => {
		const emp = ghost.employeeId ? employees.find(e => e.id === ghost.employeeId) : null
		const time = `${String(Math.floor(ghost.sm / 60)).padStart(2, '0')}:${String(ghost.sm % 60).padStart(2, '0')}`
		setGhost(null)
		onAddAppointment({ date: sel.format('YYYY-MM-DD'), time, employee: emp })
	}

	const monthTotal = mode === 'month'
		? monthCells(sel.startOf('month')).reduce((t, c) => (c.inMonth ? t + getVisits(c.date, null).length : t), 0)
		: 0

	return (
		<Box sx={{
			height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, position: 'relative',
			bgcolor: BG, color: TEXT, fontFamily: FONT, overflow: 'hidden',
		}}>
			<McHeader
				merchant={merchant}
				title={titleAnchor.format('MMMM')}
				sub={titleAnchor.format('YYYY')}
				monthOpen={monthOpen}
				monthMode={mode === 'month'}
				onToggleMonth={() => { setMonthAnchor(sel.startOf('month')); setMonthOpen(!monthOpen) }}
				onMove={move}
				onToday={goToday}
				todayDisabled={isToday && !monthOpen}
				onMenu={onOpenSidebar}
			/>
			{mode === 'month' ? null : monthOpen
				? <McMonthGrid monthAnchor={monthAnchor} sel={sel} now={now} onPick={pickDay} />
				: <McWeekStrip weekStart={weekStart} sel={sel} now={now} dir={dir} onPick={pickDay} />}
			<McGChips
				employees={employees}
				selectedEmployees={selectedEmployees}
				onToggleEmployee={onToggleEmployee}
				onClearEmployeeFilter={onClearEmployeeFilter}
			/>
			{/* meta bar + mode segment */}
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', p: '8px 16px 6px', flex: 'none' }}>
				<Box sx={{ display: 'flex', alignItems: 'baseline', gap: '7px', minWidth: 0 }}>
					<Box component="strong" sx={{ fontSize: '13px', fontWeight: 750, color: TEXT, whiteSpace: 'nowrap' }}>
						{mode === 'month' ? sel.format('MMMM YYYY') : `${dayLabel(sel)}${isToday ? ' · Today' : ''}`}
					</Box>
					<Box component="span" sx={{ fontSize: '12px', fontWeight: 650, color: MUTED, whiteSpace: 'nowrap' }}>
						{loading ? 'Loading…' : mode === 'month'
							? `${monthTotal} appointments`
							: dayVisits.length === 0 ? 'No appointments' : `${dayVisits.length} ${dayVisits.length === 1 ? 'appointment' : 'appointments'}`}
					</Box>
				</Box>
				<Box role="tablist" aria-label="Day layout" sx={{
					display: 'flex', gap: '2px', bgcolor: '#FFFFFF', border: `1.5px solid ${BORDER}`,
					borderRadius: '10px', p: '2.5px', flex: 'none',
				}}>
					{[
						{ id: 'list', icon: 'list', label: 'Schedule list' },
						{ id: 'grid', icon: 'cols', label: 'Timeline' },
						{ id: 'month', icon: 'month', label: 'Month overview' },
					].map(m => (
						<Box component="button" type="button" key={m.id} aria-label={m.label}
							onClick={() => {
								onModeChange(m.id)
								setGhost(null)
								if (m.id === 'month') setMonthOpen(false)
							}}
							sx={{
								...btnReset, width: 34, height: 28, borderRadius: '7px', color: MUTED,
								display: 'flex', alignItems: 'center', justifyContent: 'center',
								...(mode === m.id ? { bgcolor: PRIMARY_SOFT, color: PRIMARY_DEEP } : {}),
							}}>
							<McIc name={m.icon} size={15} sw={1.5} />
						</Box>
					))}
				</Box>
			</Box>
			{/* content */}
			{mode === 'month' ? (
				<McMonthView
					sel={sel} now={now} dir={dir} getVisits={getVisits}
					onPick={(d) => {
						if (d.isSame(sel, 'day')) { onModeChange('list'); return }
						setDir(d.isBefore(sel) ? -1 : 1)
						onDateChange(d)
					}}
					onOpenDay={() => onModeChange('list')}
					onOpenAppt={onVisitClick}
					onBook={bookOn}
				/>
			) : (
				<Box ref={scrollRef} sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
					{mode === 'list'
						? <McDayList visits={dayVisits} isToday={isToday} now={now} onOpen={onVisitClick} />
						: <McTimeline
							sel={sel} isToday={isToday} now={now} lanes={lanes} getVisits={getVisits}
							ghost={ghost} onTap={setGhost} onGhost={bookSlot} onOpen={onVisitClick}
							merchant={merchant}
						/>}
				</Box>
			)}
			{mode === 'grid' && !ghost && (
				<Box sx={{
					position: 'absolute', left: '50%', transform: 'translateX(-50%)', bottom: 88, zIndex: 39,
					bgcolor: 'rgba(46, 42, 64, 0.82)', color: '#FFFFFF', fontSize: '11px', fontWeight: 700,
					p: '6px 12px', borderRadius: '99px', pointerEvents: 'none', whiteSpace: 'nowrap',
				}}>
					Tap an open slot to book
				</Box>
			)}
			{mode !== 'month' && (
				<Box component="button" type="button" aria-label="Add appointment" onClick={bookOn}
					sx={{
						...btnReset, position: 'fixed', right: 18, bottom: 84, zIndex: 40, width: 56, height: 56,
						borderRadius: '18px', bgcolor: PRIMARY, color: '#FFFFFF', display: 'flex',
						alignItems: 'center', justifyContent: 'center',
						boxShadow: '0 8px 22px rgba(124, 107, 217, 0.45)',
						'&:active': { bgcolor: PRIMARY_DEEP },
					}}>
					<CalIc name="plus" size={20} sw={2} />
				</Box>
			)}
		</Box>
	)
}
